// src/pages/ResultsPage.js
import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";

import ResultsModeSlider from "../components/results/ResultsModeSlider";

import ECGHeart from "../components/icons/ECGHeart";
import Pulmonology from "../components/icons/Pulmonology";
import RespiratoryRate from "../components/icons/RespiratoryRate";
import PulseScanResults from "../components/results/PulseScanResults";
import SpirometryResults from "../components/results/SpirometryResults";
import RespiratoryResults from "../components/results/RespiratoryResults";
import { usePatient } from "../context/PatientContext";
import { normalizeId } from "../utils/patient/patientStoragePath";

function ResultsPage({ db, user }) {
  const { t, i18n } = useTranslation();

  const [mode, setMode] = useState("spiro"); //  spiro | resp | pulse

  // read patient context
  const { activeId } = usePatient();
  const subjectId = useMemo(() => normalizeId(activeId) || null, [activeId]);

  useEffect(() => {
    trackEvent("results_view", { lang: i18n.language || null });
  }, [i18n.language]);

  const items = useMemo(
    () => [
      {
        value: "spiro",
        icon: <RespiratoryRate className="w-7 h-7" />,
        label: "Spirometry",
      },
      {
        value: "resp",
        icon: <Pulmonology className="w-7 h-7" />,
        label: "Respiratory",
      },
      {
        value: "pulse",
        icon: <ECGHeart className="w-7 h-7" />,
        label: "Pulse",
      },
    ],
    [],
  );

  useEffect(() => {
    trackEvent("results_mode_change", { mode });
  }, [mode]);

  return (
    <div className="w-full max-w-lg h-full min-h-0 p-4 flex flex-col gap-4 overflow-hidden">
      {/* IMPORTANT: flex-1 + min-h-0 so children can overflow */}
      <section className="w-full flex-1 min-h-0 flex flex-col items-center text-center">
        <h2 className="subtitle-text">
          {mode === "pulse"
            ? t("results.pulse.title", "Pulse Scan Results")
            : mode === "spiro"
              ? t("results.spiro.title", "Spirometry Scan Results")
              : t("results.resp.title", "Respiratory Scan Results")}
        </h2>

        {/* IMPORTANT: also flex-1 + min-h-0 */}
        <div className="w-full flex-1 min-h-0">
          {mode === "pulse" ? (
            <PulseScanResults db={db} uid={user?.uid} subjectId={subjectId} />
          ) : mode === "spiro" ? (
            <SpirometryResults db={db} uid={user?.uid} subjectId={subjectId} />
          ) : mode === "resp" ? (
            <RespiratoryResults db={db} uid={user?.uid} subjectId={subjectId} />
          ) : null}
        </div>
      </section>

      {/* Top selector */}
      <ResultsModeSlider value={mode} onChange={setMode} items={items} />
    </div>
  );
}

export default memo(ResultsPage);
